"use client";

import { Document, Page, Text, View, StyleSheet, Font, Image } from "@react-pdf/renderer";
import type { InvoiceData } from "@/lib/invoice-types";
import { calculateSubtotal, calculateTax, calculateTotal } from "@/lib/invoice-types";

Font.register({
  family: "Geist",
  fonts: [
    {
      src: "https://cdn.jsdelivr.net/fontsource/fonts/geist-sans@latest/latin-400-normal.ttf",
      fontWeight: 400,
    },
    {
      src: "https://cdn.jsdelivr.net/fontsource/fonts/geist-sans@latest/latin-500-normal.ttf",
      fontWeight: 500,
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    fontFamily: "Geist",
    fontSize: 9.5,
    paddingTop: 44,
    paddingRight: 44,
    paddingBottom: 40,
    paddingLeft: 44,
    color: "#202020",
    backgroundColor: "#ffffff",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 28,
  },
  logoWrap: {
    width: "50%",
    alignItems: "flex-start",
  },
  logo: {
    width: 92,
    height: 28,
    objectFit: "contain",
  },
  headerTitle: {
    fontSize: 48,
    fontWeight: 500,
    color: "#000",
    letterSpacing: 0.4,
  },
  divider: {
    borderBottomWidth: 1.2,
    borderBottomColor: "#565656",
    marginBottom: 14,
  },
  topInfo: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 14,
  },
  topInfoBlock: {
    width: "48%",
  },
  sectionLabel: {
    fontSize: 9,
    fontWeight: 500,
    marginBottom: 3,
    color: "#202020",
  },
  lineText: {
    fontSize: 9.3,
    color: "#303030",
    lineHeight: 1.35,
  },
  lineStrong: {
    fontSize: 9.6,
    fontWeight: 500,
    marginBottom: 2,
    color: "#1f1f1f",
  },
  metaRow: {
    flexDirection: "row",
    borderTopWidth: 1.2,
    borderBottomWidth: 1.2,
    borderTopColor: "#565656",
    borderBottomColor: "#565656",
    paddingTop: 13,
    paddingBottom: 14,
    marginBottom: 14,
  },
  metaCell: {
    width: "25%",
    paddingRight: 8,
  },
  metaLabel: {
    fontSize: 8.2,
    fontWeight: 500,
    marginBottom: 4,
    color: "#2c2c2c",
  },
  metaValue: {
    fontSize: 10.1,
    fontWeight: 500,
    color: "#1f1f1f",
  },
  partyRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomWidth: 1.2,
    borderBottomColor: "#565656",
    paddingBottom: 12,
    marginBottom: 14,
  },
  partyBlock: {
    width: "48%",
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1.2,
    borderBottomColor: "#565656",
    paddingTop: 6,
    paddingBottom: 6,
  },
  tableHeaderText: {
    fontSize: 8.2,
    fontWeight: 500,
    color: "#2d2d2d",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#565656",
    paddingTop: 5,
    paddingBottom: 5,
  },
  colIndex: { width: "5%" },
  colDescription: { width: "45%", paddingRight: 8 },
  colQty: { width: "12%", textAlign: "center" },
  colPrice: { width: "14%", textAlign: "right", paddingRight: 6 },
  colTax: { width: "10%", textAlign: "right", paddingRight: 4 },
  colTotal: { width: "14%", textAlign: "right" },
  cellText: {
    fontSize: 9.2,
    color: "#2a2a2a",
  },
  cellTextBold: {
    fontSize: 9.2,
    fontWeight: 500,
    color: "#222222",
  },
  lowerSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 12,
  },
  lowerLeft: {
    width: "56%",
    paddingRight: 14,
  },
  lowerRight: {
    width: "33%",
  },
  notesWrap: {
    marginTop: 2,
  },
  notesLabel: {
    fontSize: 9,
    fontWeight: 500,
    marginBottom: 3,
    color: "#262626",
  },
  notesText: {
    fontSize: 9.1,
    color: "#343434",
    lineHeight: 1.35,
  },
  totalsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomWidth: 1,
    borderBottomColor: "#565656",
    paddingTop: 5,
    paddingBottom: 5,
  },
  totalsLabel: {
    fontSize: 9.3,
    fontWeight: 500,
    color: "#2b2b2b",
  },
  totalsValue: {
    fontSize: 9.3,
    color: "#2b2b2b",
  },
  totalsGrandRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomWidth: 1.4,
    borderBottomColor: "#565656",
    paddingTop: 7,
    paddingBottom: 7,
  },
  totalsGrandLabel: {
    fontSize: 10.2,
    fontWeight: 500,
    color: "#202020",
  },
  totalsGrandValue: {
    fontSize: 10.2,
    fontWeight: 500,
    color: "#202020",
  },
  signatureRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 46,
  },
  signatureBlock: {
    width: "42%",
  },
  signatureLabel: {
    fontSize: 9,
    fontWeight: 500,
    marginBottom: 2,
    color: "#252525",
  },
  signatureValue: {
    fontSize: 9.2,
    color: "#333333",
  },
});

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("sv-SE", {
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatCurrencyWithSymbol(amount: number): string {
  return new Intl.NumberFormat("sv-SE", {
    style: "currency",
    currency: "SEK",
    minimumFractionDigits: 2,
  }).format(amount);
}

export default function InvoicePDF({ data }: { data: InvoiceData }) {
  const subtotal = calculateSubtotal(data.items);
  const tax = calculateTax(subtotal, data.taxRate);
  const total = calculateTotal(subtotal, tax);
  const logoSrc = "/solvy.png";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.logoWrap}>
            <Image src={logoSrc} style={styles.logo} />
          </View>
          <View>
            <Text style={styles.headerTitle}>FAKTURA</Text>
          </View>
        </View>

        <View style={styles.topInfo}>
          <View style={styles.topInfoBlock}>
            <Text style={styles.lineStrong}>{data.fromCompany || "Ditt Företag"}</Text>
            {data.fromOrgNumber ? <Text style={styles.lineText}>Org nr: {data.fromOrgNumber}</Text> : null}
            {data.fromAddress ? <Text style={styles.lineText}>{data.fromAddress}</Text> : null}
            {data.fromCity ? <Text style={styles.lineText}>{data.fromCity}</Text> : null}
          </View>
          <View style={styles.topInfoBlock}>
            <Text style={styles.sectionLabel}>Kontakt</Text>
            {data.fromEmail ? <Text style={styles.lineText}>{data.fromEmail}</Text> : null}
            {data.fromPhone ? <Text style={styles.lineText}>{data.fromPhone}</Text> : null}
          </View>
        </View>

        <View style={styles.metaRow}>
          <View style={styles.metaCell}>
            <Text style={styles.metaLabel}>Att betala</Text>
            <Text style={styles.metaValue}>{formatCurrencyWithSymbol(total)}</Text>
          </View>
          <View style={styles.metaCell}>
            <Text style={styles.metaLabel}>Förfallodatum</Text>
            <Text style={styles.metaValue}>{data.dueDate}</Text>
          </View>
          <View style={styles.metaCell}>
            <Text style={styles.metaLabel}>Fakturanummer</Text>
            <Text style={styles.metaValue}>{data.invoiceNumber}</Text>
          </View>
          <View style={styles.metaCell}>
            <Text style={styles.metaLabel}>Fakturadatum</Text>
            <Text style={styles.metaValue}>{data.invoiceDate}</Text>
          </View>
        </View>

        <View style={styles.partyRow}>
          <View style={styles.partyBlock}>
            <Text style={styles.sectionLabel}>Faktureras till</Text>
            <Text style={styles.lineStrong}>{data.toCompany || "Kundföretag"}</Text>
            {data.toAddress ? <Text style={styles.lineText}>{data.toAddress}</Text> : null}
            {data.toCity ? <Text style={styles.lineText}>{data.toCity}</Text> : null}
            {data.toEmail ? <Text style={styles.lineText}>{data.toEmail}</Text> : null}
          </View>
          <View style={styles.partyBlock}>
            <Text style={styles.sectionLabel}>Från</Text>
            <Text style={styles.lineStrong}>{data.fromCompany || "Ditt Företag"}</Text>
            {data.fromOrgNumber ? <Text style={styles.lineText}>Org nr: {data.fromOrgNumber}</Text> : null}
            {data.fromAddress ? <Text style={styles.lineText}>{data.fromAddress}</Text> : null}
            {data.fromCity ? <Text style={styles.lineText}>{data.fromCity}</Text> : null}
            {data.fromEmail ? <Text style={styles.lineText}>{data.fromEmail}</Text> : null}
          </View>
        </View>

        <View style={styles.tableHeader}>
          <Text style={{ ...styles.tableHeaderText, ...styles.colIndex }}>#</Text>
          <Text style={{ ...styles.tableHeaderText, ...styles.colDescription }}>Beskrivning av vara/tjänst</Text>
          <Text style={{ ...styles.tableHeaderText, ...styles.colQty }}>Antal</Text>
          <Text style={{ ...styles.tableHeaderText, ...styles.colPrice }}>Pris (SEK)</Text>
          <Text style={{ ...styles.tableHeaderText, ...styles.colTax }}>Moms</Text>
          <Text style={{ ...styles.tableHeaderText, ...styles.colTotal }}>Summa (SEK)</Text>
        </View>

        {data.items.map((item, index) => (
          <View key={item.id} style={styles.tableRow}>
            <Text style={{ ...styles.cellText, ...styles.colIndex }}>{index + 1}</Text>
            <Text style={{ ...styles.cellTextBold, ...styles.colDescription }}>{item.description || "—"}</Text>
            <Text style={{ ...styles.cellText, ...styles.colQty }}>{item.quantity}</Text>
            <Text style={{ ...styles.cellText, ...styles.colPrice }}>{formatCurrency(item.unitPrice)}</Text>
            <Text style={{ ...styles.cellText, ...styles.colTax }}>{data.taxRate}%</Text>
            <Text style={{ ...styles.cellTextBold, ...styles.colTotal }}>{formatCurrency(item.quantity * item.unitPrice)}</Text>
          </View>
        ))}

        <View style={styles.lowerSection}>
          <View style={styles.lowerLeft}>
            {data.notes ? (
              <View style={styles.notesWrap}>
                <Text style={styles.notesLabel}>Anteckningar</Text>
                <Text style={styles.notesText}>{data.notes}</Text>
              </View>
            ) : null}
          </View>

          <View style={styles.lowerRight}>
            <View style={styles.totalsRow}>
              <Text style={styles.totalsLabel}>Delsumma</Text>
              <Text style={styles.totalsValue}>{formatCurrency(subtotal)}</Text>
            </View>
            <View style={styles.totalsRow}>
              <Text style={styles.totalsLabel}>Moms</Text>
              <Text style={styles.totalsValue}>{formatCurrency(tax)}</Text>
            </View>
            <View style={styles.totalsGrandRow}>
              <Text style={styles.totalsGrandLabel}>Totalt</Text>
              <Text style={styles.totalsGrandValue}>{formatCurrency(total)}</Text>
            </View>
          </View>
        </View>

        <View style={styles.signatureRow}>
          <View style={styles.signatureBlock}>
            <Text style={styles.signatureLabel}>Godkänd av</Text>
            <Text style={styles.signatureValue}>{data.toCompany || "Kund"}</Text>
          </View>
          <View style={styles.signatureBlock}>
            <Text style={styles.signatureLabel}>Signatur</Text>
            <Text style={styles.signatureValue}>{data.fromCompany || "Ditt Företag"}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}
